/**
 * 往当前白板页里插一张图（位图或矢量图）。
 *
 * 和「导入课件」的区别：课件是一页一张、铺满整页；这里是**落在你放手的位置**，
 * 不新建页。老系统里往板书中间贴一张图是拖进去的，
 * 走导入课件那条路会强行新建一页，把讲课节奏打断。
 */

import type { Client } from './client'
import { MsgType } from './messages'
import { isSvgFile, sanitizeSvg, svgViewBox, MAX_SVG_BYTES } from './svgAsset'
import { uploadWhiteboardImage } from './upload'
import type { WBPoint } from './whiteboard-types'

/** 位图大小上限。再大的图对板书没有意义，只会拖慢所有人的加载。 */
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024

/** 插入时图片占页面宽度的比例。太大会盖住板书，太小看不清。 */
const DEFAULT_WIDTH_RATIO = 0.36

export interface InsertResult {
  ok: boolean
  reason?: string
}

/** 这个文件能不能往白板上插。 */
export function canInsert(file: File): boolean {
  return isSvgFile(file) || file.type.startsWith('image/')
}

/**
 * 把一个文件插到指定位置。
 *
 * at 是归一化坐标（0-1），表示图的**中心**落在哪里 ——
 * 用中心而不是左上角，是因为拖放时指针停在哪，人就期望图出现在哪。
 */
export async function insertImageAt(
  client: Client,
  page: number,
  selfId: number,
  file: File,
  at: WBPoint,
  /**
   * 画布的 高/宽。用来把图的比例折算到归一化坐标里。
   *
   * 必须由调用方按实际画布量出来传进来，不能假定 4:3 ——
   * 画布高度是 min(容器高, 宽×3/4)，容器矮的时候取的是容器高，
   * 实测宽屏下是 1.72:1 而不是 1.33:1。按 4:3 折算的话，
   * 一张 3:2 的图会被画成 1.95:1，明显压扁。
   */
  pageAspect = 3 / 4,
): Promise<InsertResult> {
  if (!canInsert(file)) return { ok: false, reason: '只能插入图片或 SVG 矢量图' }

  const svg = isSvgFile(file)
  if (svg && file.size > MAX_SVG_BYTES) return { ok: false, reason: '矢量图超过 2MB' }
  if (!svg && file.size > MAX_IMAGE_BYTES) return { ok: false, reason: '图片超过 8MB' }

  // 先把内容准备好，拿到原始宽高比 —— 图元的框要按比例定，
  // 不然插进去的图会被拉扁。
  let blob: Blob
  let ratio: number
  if (svg) {
    const clean = sanitizeSvg(await file.text())
    if (!clean.ok) return { ok: false, reason: clean.reason }
    const box = svgViewBox(clean.svg)
    blob = new Blob([clean.svg], { type: 'image/svg+xml' })
    ratio = box.h / box.w
  } else {
    const dim = await imageSize(file)
    if (!dim) return { ok: false, reason: '这张图片解不开' }
    blob = file
    ratio = dim.h / dim.w
  }

  const box = boxAround(at, DEFAULT_WIDTH_RATIO, ratio, pageAspect)
  const objId = await addObjAndWait(client, page, box, selfId, svg ? 'svg' : 'image')
  if (objId <= 0) return { ok: false, reason: '服务端没有分配对象号' }

  try {
    await uploadWhiteboardImage(client, objId, page, blob)
  } catch (e) {
    // 上传半路断线：图元已经建了但内容没传完，如实告诉用户，别装作插入成功。
    return { ok: false, reason: e instanceof Error ? e.message : '图片上传失败' }
  }
  return { ok: true }
}

/** 读一张位图的像素尺寸。 */
function imageSize(file: File): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img.naturalWidth > 0 ? { w: img.naturalWidth, h: img.naturalHeight } : null)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })
}

/**
 * 以 at 为中心算出图元的框，并保证整张图留在页内。
 *
 * 页面是 4:3 的，而归一化坐标两个轴都是 0-1 —— 也就是说
 * 同样的数值在纵向代表的实际长度更短。要保持原图比例不被拉扁，
 * 纵向的数值要按 4/3 折算回来。
 */
export function boxAround(
  at: WBPoint,
  widthRatio: number,
  aspect: number,
  pageAspect = 3 / 4,
): { x0: number; y0: number; x1: number; y1: number } {
  const w = Math.min(widthRatio, 0.9)
  // aspect 是图的 高/宽；除以页面的 高/宽，得到归一化坐标下该占的纵向长度。
  const h = Math.min((w * aspect) / pageAspect, 0.9)
  // 夹住中心点，让整张图不出页 —— 拖到边角松手也不该有一半在外面。
  const cx = clamp(at.x, w / 2, 1 - w / 2)
  const cy = clamp(at.y, h / 2, 1 - h / 2)
  return { x0: cx - w / 2, y0: cy - h / 2, x1: cx + w / 2, y1: cy + h / 2 }
}

function clamp(v: number, lo: number, hi: number): number {
  if (lo > hi) return 0.5 // 图比页还大时居中
  return v < lo ? lo : v > hi ? hi : v
}

/** 建图元并等服务端回填对象号。 */
function addObjAndWait(
  client: Client,
  page: number,
  box: { x0: number; y0: number; x1: number; y1: number },
  selfId: number,
  kind: 'image' | 'svg',
): Promise<number> {
  return new Promise((resolve) => {
    const off = client.on<{ op: string; obj?: { id: number; ownerId: number; kind: string } }>(
      MsgType.WBOp,
      (d) => {
        if (d.op === 'add' && d.obj?.kind === kind && d.obj.ownerId === selfId) {
          off()
          resolve(d.obj.id)
        }
      },
    )
    client.send(MsgType.WBOp, {
      page,
      op: 'add',
      obj: {
        id: 0,
        kind,
        page,
        ownerId: selfId,
        points: [
          { x: box.x0, y: box.y0 },
          { x: box.x1, y: box.y1 },
        ],
        style: {},
        z: 0,
        time: 0,
      },
    })
    // 服务端没回就别一直挂着，让调用方给出错误提示。
    setTimeout(() => {
      off()
      resolve(0)
    }, 5000)
  })
}
